import { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ChevronDown, Search, HelpCircle, Package, CreditCard, Truck, RefreshCw, Shield, Mail } from 'lucide-react';
import { SeoHead } from '../components/SeoHead';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

interface FAQItem {
  id: number;
  soru: string;
  cevap: string;
  kategori?: string;
}

const categories = [
  { id: 'all', name: 'Tümü', icon: HelpCircle },
  { id: 'Sipariş & Ödeme', name: 'Sipariş & Ödeme', icon: CreditCard },
  { id: 'Kargo & Teslimat', name: 'Kargo & Teslimat', icon: Truck },
  { id: 'İade & Değişim', name: 'İade & Değişim', icon: RefreshCw },
  { id: 'Ürünler & Stok', name: 'Ürünler & Stok', icon: Package },
  { id: 'Üyelik & Hesap', name: 'Üyelik & Hesap', icon: Shield },
];

export function FAQ() {
  const [faqData, setFaqData] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    fetch(`${API_URL}/api/sss`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setFaqData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  let filteredFAQs = faqData;
  if (selectedCategory !== 'all') {
    filteredFAQs = faqData.filter(faq => faq.kategori === selectedCategory);
  }
  if (searchQuery) {
    filteredFAQs = filteredFAQs.filter(
      faq => faq.soru.toLowerCase().includes(searchQuery.toLowerCase()) ||
             faq.cevap.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col">
      <SeoHead
        title="Sıkça Sorulan Sorular | Ladiora Boutique"
        description="Sipariş, kargo, iade ve ürünler hakkında sıkça sorulan sorular — Ladiora Boutique."
        jsonLd={faqData.length ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqData.slice(0, 40).map((q) => ({
            '@type': 'Question',
            name: q.soru,
            acceptedAnswer: { '@type': 'Answer', text: q.cevap },
          })),
        } : null}
      />
      <Header />
      <main className="flex-1">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-pink-600 to-purple-600 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="inline-flex size-20 bg-white/20 rounded-full items-center justify-center backdrop-blur-sm mb-6"><HelpCircle className="size-10" /></div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Sıkça Sorulan Sorular</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-95 mb-8">Aklınıza takılan sorulara cevaplar burada</p>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 size-6 text-gray-400" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Sorunuzu arayın..." className="w-full pl-16 pr-6 py-5 text-lg text-gray-900 rounded-2xl shadow-2xl outline-none focus:ring-4 focus:ring-pink-300" />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Categories */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Kategoriler</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <button key={cat.id} onClick={() => setSelectedCategory(cat.id)}
                  className={`p-4 rounded-xl border-2 transition-all hover:shadow-lg ${selectedCategory === cat.id ? 'border-pink-600 bg-pink-50 shadow-md' : 'border-gray-200 bg-white hover:border-pink-300'}`}>
                  <cat.icon className={`size-8 mx-auto mb-2 ${selectedCategory === cat.id ? 'text-pink-600' : 'text-gray-600'}`} />
                  <span className={`text-sm font-semibold ${selectedCategory === cat.id ? 'text-pink-600' : 'text-gray-700'}`}>{cat.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-4xl mx-auto">
            {loading ? (
              <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto mb-4"></div><p className="text-gray-600">Yükleniyor...</p></div>
            ) : filteredFAQs.length === 0 ? (
              <div className="text-center py-12">
                <HelpCircle className="size-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Sonuç bulunamadı</h3>
                <p className="text-gray-600">Aradığınız soruya cevap bulamadık. Lütfen farklı bir arama yapın veya bizimle iletişime geçin.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredFAQs.map((faq, index) => (
                  <div key={faq.id || index} className="bg-white rounded-xl shadow-sm border-2 border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                    <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors">
                      <div className="flex-1 pr-4">
                        {faq.kategori && <div className="text-xs font-semibold text-pink-600 mb-1">{faq.kategori}</div>}
                        <h3 className="text-lg font-semibold text-gray-900">{faq.soru}</h3>
                      </div>
                      <ChevronDown className={`size-6 text-gray-400 flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
                    </button>
                    {openIndex === index && (
                      <div className="px-6 py-5 bg-gray-50 border-t border-gray-100"><p className="text-gray-700 leading-relaxed">{faq.cevap}</p></div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Still Have Questions */}
          <div className="mt-16 bg-gradient-to-r from-pink-600 to-purple-600 rounded-2xl p-12 text-center text-white">
            <Mail className="size-16 mx-auto mb-6 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Hala Sorunuz Var mı?</h2>
            <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto">Bulamadığınız sorular için bizimle iletişime geçin!</p>
            <div className="flex flex-col md:flex-row gap-4 justify-center">
              <a href="/iletisim" className="bg-white text-pink-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"><Mail className="size-5" />Bize Yazın</a>
              <a href="tel:+905434671896" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"><HelpCircle className="size-5" />Bizi Arayın</a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <a href="/siparis-takip" className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-pink-300 hover:shadow-md transition-all group">
              <Package className="size-10 text-pink-600 mb-3 group-hover:scale-110 transition-transform" /><h3 className="text-lg font-bold text-gray-900 mb-2">Sipariş Takip</h3><p className="text-gray-600 text-sm">Siparişinizin durumunu kontrol edin</p>
            </a>
            <a href="/iletisim" className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-pink-300 hover:shadow-md transition-all group">
              <Mail className="size-10 text-pink-600 mb-3 group-hover:scale-110 transition-transform" /><h3 className="text-lg font-bold text-gray-900 mb-2">İletişim</h3><p className="text-gray-600 text-sm">Bizimle iletişime geçin</p>
            </a>
            <a href="/" className="bg-white rounded-xl p-6 shadow-sm border-2 border-gray-100 hover:border-pink-300 hover:shadow-md transition-all group">
              <HelpCircle className="size-10 text-pink-600 mb-3 group-hover:scale-110 transition-transform" /><h3 className="text-lg font-bold text-gray-900 mb-2">Alışverişe Başla</h3><p className="text-gray-600 text-sm">Ürünlerimize göz atın</p>
            </a>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
